import posthog from 'posthog-js'
import { AnalyticsBrowser } from '@segment/analytics-next'
import tenants from '@/generated/tenants.json'
import { DEFAULT_TENANT_ID } from '@/constants/tenant'

type PosthogIntegrationSettings = {
  apiKey: string
  apiHost?: string
}

function getTenant() {
  const hostname = window.location.hostname

  if (hostname.includes('localhost') || hostname.includes('teamfeedr.vercel.app')) {
    return tenants.find((t) => t.id === DEFAULT_TENANT_ID)
  }

  return tenants.find((t) => t.domain === hostname)
}

function initPostHog(posthogConfig?: PosthogIntegrationSettings | null, segment?: any) {
  const apiKey = posthogConfig?.apiKey || process.env.NEXT_PUBLIC_POSTHOG_KEY

  if (!apiKey) return

  const apiHost =
    posthogConfig?.apiHost || process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com'

  posthog.init(apiKey, {
    api_host: apiHost,
    // Sync with Segment
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    segment: segment as any,
    // Segment handles pageviews via analytics.page()
    capture_pageview: false,
    // Enable pageleave tracking for accurate bounce rate and duration
    // capture_pageleave: true,

    // Enable person processing for user identification
    person_profiles: 'identified_only',
    loaded: () => {
      // When PostHog loads, call analytics.page() for initial pageview
      segment?.page?.()
    },
  })
}

function getReferringDomain(referrer?: string | null) {
  if (!referrer) return null

  try {
    return new URL(referrer).hostname || null
  } catch {
    return null
  }
}

function initAnalytics() {
  const tenant = getTenant()
  if (!tenant) return

  const integrations = tenant.integrations as {
    segment?: { clientWriteKey: string } | null
    posthog?: PosthogIntegrationSettings | null
  } | null

  const posthogConfig = integrations?.posthog

  const segmentWriteKey = integrations?.segment?.clientWriteKey

  if (segmentWriteKey) {
    AnalyticsBrowser.load({ writeKey: segmentWriteKey }).then(([analytics]) => {
      ;(window as any).analytics = analytics

      // Add source middleware to enrich all events with referrer information
      analytics.addSourceMiddleware?.(({ payload, next }: any) => {
        const obj = payload.obj ?? {}
        const referrer = obj.context?.page?.referrer ?? document.referrer ?? null

        obj.properties = {
          ...obj.properties,
          referrer: obj.properties?.referrer ?? referrer,
          referring_domain: obj.properties?.referring_domain ?? getReferringDomain(referrer),
        }

        payload.obj = obj
        next(payload)
      })

      // Initialize PostHog after Segment is ready
      initPostHog(posthogConfig, analytics)
    })
  } else {
    // No Segment, initialize PostHog without it
    initPostHog(posthogConfig)
  }
}

initAnalytics()
